/* eslint-disable jsx-a11y/label-has-associated-control */
import { Plus, Trash } from '@saleshandy/icons';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { accessibleOnClick } from '../../../../../../../shared/utils/accessible-on-click';
import StepWrapper from '../../../../../shared/stepwrapper';
import { CompanyWebsiteAndBusinessInfoProps } from '../../../type';
import AiSequenceModalFooter from '../ai-sequence-modal-footer/ai-sequence-modal-footer';
import LabelDescriptionForm from '../label-description-modal/label-description-modal';
import { validateLabelAndDescriptionLength } from '../util/helper';
import { setAiSeqChatMessage } from '../../../../../sequence-slice';

/**
 *@description
 * This component allows users to either provide a company website URL or fill in business information
 * such as the name and description. It includes validation for both input modes and allows switching
 * between the two, using an "Add Business Info" button. One method is required before form submission.
 *
 * @prop {saveCurrentStepDetails} - Function to save the current step details.
 *
 * @returns {JSX.Element} A form with website URL and optional business info fields.
 */

const ComapanyWebsiteAndBusinessInfo: React.FC<CompanyWebsiteAndBusinessInfoProps> = ({
  saveCurrentStepDetails,
}): JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null);
  const dispatch = useDispatch();

  const [formState, setFormState] = useState({
    websiteUrl: '',
    urlError: '',
    showBusinessInfo: false,
    isValidBusinessInfo: false,
    companyName: '',
    description: '',
    isUrlValid: false,
    showValidationErrors: false,
    nameError: '',
    descError: '',
  });
  const validateLabelAndDescription = useCallback(
    (
      label: string,
      description: string,
      field: 'label' | 'description' | 'both' = 'both',
    ) => {
      const {
        isValid,
        labelError,
        descriptionError,
      } = validateLabelAndDescriptionLength({
        label,
        description,
        labelMinLength: 1,
        labelMaxLength: 200,
        descriptionMinLength: 10,
        descriptionMaxLength: 1000,
        labelErrorMsg: 'Company name must be',
        descriptionErrorMsg: 'Description must be',
        validateLabel: field === 'label' || field === 'both',
        validateDescription: field === 'description' || field === 'both',
      });

      setFormState((prev) => ({
        ...prev,
        ...(field === 'label' || field === 'both'
          ? { nameError: labelError }
          : {}),
        ...(field === 'description' || field === 'both'
          ? { descError: descriptionError }
          : {}),
        isValidBusinessInfo: isValid,
      }));

      return isValid;
    },
    [],
  );

  // ** Website URL Validation
  const validateUrl = useCallback(() => {
    if (!formState.showBusinessInfo) {
      const cleanedUrl =
        formState.websiteUrl
          ?.replace(/^https?:\/\//, '')
          ?.replace(/^www\./i, '')
          ?.split('/')?.[0] || '';
      const urlRegex = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}((\/|\?).*)?$/;

      if (!urlRegex?.test(cleanedUrl)) {
        setFormState((prevState) => ({
          ...prevState,
          urlError: 'Please enter a valid website URL',
          isUrlValid: false,
        }));
        return false;
      }

      setFormState((prevState) => ({
        ...prevState,
        urlError: '',
        isUrlValid: true,
      }));
    }

    return true;
  }, [formState.websiteUrl, formState.showBusinessInfo]);

  const handleCompanyNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e?.target?.value;
      setFormState((prevState) => ({
        ...prevState,
        companyName: inputValue,
      }));

      validateLabelAndDescription(inputValue, formState.description, 'label');
    },

    [setFormState],
  );

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const inputValue = e?.target?.value;
      // setDescription(inputValue);
      setFormState((prevState) => ({
        ...prevState,
        description: inputValue,
      }));

      validateLabelAndDescription(
        formState.companyName,
        inputValue,
        'description',
      );
    },
    [setFormState],
  );

  // **domain name URL change handler
  const handleUrlChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e?.target?.value;
      setFormState((prevState) => ({
        ...prevState,
        websiteUrl: inputValue,
      }));

      const cleanedUrl = inputValue
        ?.replace(/^https?:\/\//, '')
        ?.replace(/^www\./i, '');
      const urlRegex = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}((\/|\?).*)?$/;

      if (urlRegex?.test(cleanedUrl)) {
        setFormState((prevState) => ({
          ...prevState,
          urlError: '',
          isUrlValid: true,
        }));
      }
    },
    [],
  );

  // **Submit handler
  const handleSubmit = useCallback(() => {
    setFormState((prev) => ({
      ...prev,
      showValidationErrors: true,
    }));
    const {
      companyName,
      description,
      websiteUrl,
      showBusinessInfo,
    } = formState;
    if (showBusinessInfo) {
      validateLabelAndDescription(companyName, description);
    }
    const website =
      websiteUrl
        ?.replace(/^https?:\/\//, '')
        ?.split('/')?.[0]
        ?.split('?')?.[0]
        ?.split('#')?.[0] || '';

    const payload = {
      companyInfo: {
        ...(websiteUrl ? { website } : { name: companyName, description }),
      },
    };
    if (
      formState.showBusinessInfo
        ? validateLabelAndDescription(companyName, description)
        : validateUrl()
    ) {
      dispatch(setAiSeqChatMessage(showBusinessInfo ? companyName : website));
      saveCurrentStepDetails(payload);
    }
  }, [
    formState.isValidBusinessInfo,
    formState?.companyName,
    formState?.description,
    formState?.websiteUrl,
    validateUrl,
    saveCurrentStepDetails,
  ]);

  // **Close Business info modal
  const handleBusinessinfoClose = useCallback(() => {
    setFormState((prevState) => ({
      ...prevState,
      showBusinessInfo: false,
      companyName: '',
      description: '',
      isValidBusinessInfo: false,
      nameError: '',
      descError: '',
    }));
  }, [setFormState]);

  // **Toggle Business info modal
  const toggleBusinessInfo = useCallback(() => {
    setFormState((prevState) => ({
      ...prevState,
      urlError: '',
      isUrlValid: false,
      websiteUrl: '',
      showBusinessInfo: !prevState.showBusinessInfo,
    }));
  }, [setFormState]);

  const isContinueDisabled = useCallback(() => {
    if (formState.showBusinessInfo) {
      return (
        formState.companyName.trim().length === 0 ||
        formState.description.trim().length === 0
      );
    }

    const inputWithoutPrefix = formState.websiteUrl
      ?.replace(/^https?:\/\//, '')
      ?.trim();
    return inputWithoutPrefix.length === 0;
  }, [
    formState.showBusinessInfo,
    formState.companyName,
    formState.description,
    formState.websiteUrl,
  ]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (inputRef.current && !formState.showBusinessInfo) {
        inputRef.current.focus();
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, [formState.showBusinessInfo]);

  return (
    <StepWrapper heading="Enter Your Company Website or Business Info">
      <form
        className="company-website-form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <div
          className={`input-section ${
            formState.showBusinessInfo ? 'custom-opacity' : ''
          }`}
        >
          <label htmlFor="website-url">Website</label>
          <div className="url-input-container">
            <div
              className={`url-input-wrapper ${
                // eslint-disable-next-line no-nested-ternary
                formState.showBusinessInfo
                  ? 'disable'
                  : formState.urlError
                  ? 'error'
                  : ''
              }`}
            >
              <div className="company-website-input">
                <input
                  disabled={formState.showBusinessInfo}
                  type="text"
                  id="website-url"
                  placeholder="Enter Website"
                  value={formState.websiteUrl}
                  onChange={handleUrlChange}
                  ref={inputRef}
                />
              </div>
            </div>
            {formState.urlError && (
              <p className="error-message">{formState.urlError}</p>
            )}
          </div>
        </div>

        <div className="separator">
          <span>OR</span>
        </div>

        <div
          className={`d-flex business-info-container ${
            formState.showBusinessInfo ? 'business-info-container-show' : ''
          }`}
        >
          {formState.showBusinessInfo && (
            <div className="business-info-header">
              <span className="">Business Information</span>
              <span
                className="icon-trash"
                {...accessibleOnClick(handleBusinessinfoClose)}
              >
                <Trash />
              </span>
            </div>
          )}
          {formState.showBusinessInfo && (
            <LabelDescriptionForm
              nameLabel="Company Name"
              onNameChange={handleCompanyNameChange}
              onDescriptionChange={handleDescriptionChange}
              showValidationError={formState.showValidationErrors}
              nameValue={formState.companyName}
              descriptionValue={formState.description}
              onValidate={validateLabelAndDescription}
              nameError={formState.nameError}
              descError={formState.descError}
            />
          )}
        </div>
        <div
          className={` add-business-info-section ${
            formState.showBusinessInfo ? 'add-business-info' : ''
          }`}
        >
          <button
            type="button"
            className={`add-business-info-btn ${
              formState.showBusinessInfo ? 'disable-btn' : ''
            }`}
            disabled={formState.showBusinessInfo}
            onMouseDown={toggleBusinessInfo}
          >
            <Plus />
            <span>Add Business info</span>
          </button>
        </div>

        {/* Ai Sequence Footer */}

        <AiSequenceModalFooter
          disableContinueBtn={isContinueDisabled()}
          onClickContinue={handleSubmit}
        />
      </form>
    </StepWrapper>
  );
};
export default memo(ComapanyWebsiteAndBusinessInfo);
